import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RRule } from 'rrule';
import type { Database } from '@/integrations/supabase/types';

export type TaskStatus = 'Scheduled' | 'Due' | 'Overdue' | 'Done' | 'Skipped';
export type Task = Database['public']['Tables']['tasks']['Row'] & { status: TaskStatus };

interface TaskFilters {
  relationType?: string;
  relationId?: string;
}

interface CreateTaskData {
  title: string;
  description?: string;
  cadence: string;
  rrule?: string;
  owner?: string;
  due_next: string;
  status: TaskStatus;
  related_to_type?: string;
  related_to_id?: string;
  org_id: string;
}

interface UpdateTaskData {
  title?: string;
  description?: string;
  cadence?: string;
  rrule?: string;
  owner?: string;
  due_next?: string;
  status?: TaskStatus;
  related_to_type?: string;
  related_to_id?: string;
}

const normalizeStatus = (status?: string | null): TaskStatus => {
  switch (status) {
    case 'Scheduled':
    case 'Due':
    case 'Overdue':
    case 'Done':
    case 'Skipped':
      return status;
    default:
      return 'Scheduled';
  }
};

export const useTasks = (orgId?: string, filters?: TaskFilters) => {
  const queryClient = useQueryClient();

  const tasksQuery = useQuery({
    queryKey: ['tasks', orgId, filters?.relationType, filters?.relationId],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('tasks')
        .select('*')
        .eq('org_id', orgId);

      if (filters?.relationType && filters?.relationId) {
        query = query
          .eq('related_to_type', filters.relationType)
          .eq('related_to_id', filters.relationId);
      }

      const { data, error } = await query.order('due_next', { ascending: true });

      if (error) throw error;

      return (data || []).map((taskRecord) => ({
        ...taskRecord,
        status: normalizeStatus(taskRecord.status),
      })) as Task[];
    },
    enabled: !!orgId,
  });

  const createTaskMutation = useMutation({
    mutationFn: async (data: CreateTaskData) => {
      const { error } = await supabase.from('tasks').insert([{
        org_id: data.org_id,
        title: data.title,
        description: data.description ?? null,
        cadence: data.cadence,
        rrule: data.rrule ?? null,
        owner: data.owner ?? null,
        due_next: data.due_next,
        status: data.status,
        related_to_type: data.related_to_type ?? null,
        related_to_id: data.related_to_id ?? null,
      }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task created successfully');
    },
    onError: (error) => {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTaskData }) => {
      const { error } = await supabase
        .from('tasks')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task updated successfully');
    },
    onError: (error) => {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the task
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Task deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      const tasks = tasksQuery.data;
      const task = tasks?.find(t => t.id === id);
      
      if (!task) throw new Error('Task not found');

      let nextDue = task.due_next;
      if (task.rrule) {
        try {
          const rule = RRule.fromString(task.rrule);
          const currentDue = new Date(task.due_next);
          const occurrences = rule.after(currentDue, true);
          if (occurrences) {
            nextDue = occurrences.toISOString();
          }
        } catch (err) {
          console.warn('Could not parse RRule for next occurrence:', err);
        }
      }

      const { error } = await supabase
        .from('tasks')
        .update({
          status: 'Done',
          due_next: nextDue,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task completed');
    },
    onError: (error) => {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    },
  });

  return {
    tasks: tasksQuery.data,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    createTask: createTaskMutation.mutate,
    updateTask: updateTaskMutation.mutate,
    deleteTask: deleteTaskMutation.mutate,
    completeTask: completeTaskMutation.mutate,
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
    isCompleting: completeTaskMutation.isPending,
  };
};
